import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionApi, documentsApi, analysisApi, chatApi } from '@/services/sessionApi';
import type { SessionData } from '@/services/sessionApi';
import { useNotify } from '@/contexts/NotificationContext';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useDocumentProgress } from '@/hooks/useDocumentProgress';

// Analysis Tools Configuration
const ANALYSIS_TOOLS = [
  { key: 'summary', icon: '📄', label: 'Özet', category: 'core', time: '2-3 dk' },
  { key: 'issues', icon: '⚠️', label: 'Sorunlar', category: 'core', time: '3-5 dk' },
  { key: 'evidence', icon: '🔍', label: 'Delil', category: 'core', time: '4-6 dk' },
  { key: 'timeline', icon: '⏰', label: 'Zaman', category: 'planning', time: '3-4 dk' },
  { key: 'questions', icon: '❓', label: 'Sorular', category: 'planning', time: '2-3 dk' },
  { key: 'strategy', icon: '🎯', label: 'Strateji', category: 'strategy', time: '5-8 dk' },
  { key: 'contradictions', icon: '⚡', label: 'Çelişki', category: 'strategy', time: '4-6 dk' },
  { key: 'arguments', icon: '🗺️', label: 'Argüman', category: 'strategy', time: '4-5 dk' },
  { key: 'opponent', icon: '👥', label: 'Karşı Taraf', category: 'advanced', time: '3-5 dk' },
  { key: 'risks', icon: '📊', label: 'Risk', category: 'advanced', time: '4-6 dk' },
  { key: 'negotiation', icon: '🤝', label: 'Müzakere', category: 'advanced', time: '4-5 dk' },
  { key: 'mindmap', icon: '🧠', label: 'Değerlendirme', category: 'specialized', time: '5-7 dk' },
];

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'analysis';
  content: string;
  timestamp: Date;
  analysisType?: string;
  analysisTitle?: string;
}

const NewStrategySessionPage: React.FC = () => {
  const { sessionKey } = useParams<{ sessionKey: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Core state
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Analysis state
  const [runningAnalysis, setRunningAnalysis] = useState<string | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Editable case info states
  const [editingCaseInfo, setEditingCaseInfo] = useState(false);
  const [caseInfo, setCaseInfo] = useState({
    clientName: '',
    counterParty: '',
    casePosition: ''
  });

  // Progress tracking
  const { getDocumentProgress, isProcessing } = useDocumentProgress(sessionKey || '');

  useEffect(() => {
    if (!sessionKey) {
      navigate('/');
      return;
    }

    loadSession();
    loadDocuments();
    loadChatHistory();
  }, [sessionKey]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
          console.log('Scroll applied:', {
            scrollTop: container.scrollTop,
            scrollHeight: container.scrollHeight,
            clientHeight: container.clientHeight,
            messagesCount: chatMessages.length
          });
        }
      }, 100);
    }
  };

  const loadSession = async () => {
    try {
      const sessionData = await sessionApi.getByKey(sessionKey!);
      setSession(sessionData);

      // Initialize case info state
      setCaseInfo({
        clientName: sessionData.clientName || '',
        counterParty: sessionData.counterParty || '',
        casePosition: sessionData.casePosition || ''
      });
    } catch (error: any) {
      notify.error('Oturum Hatası', error.message);
      navigate('/');
    }
  };

  const loadDocuments = async () => {
    try {
      const { documents } = await documentsApi.getBySession(sessionKey!);
      setDocuments(documents);
    } catch (error: any) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChatHistory = async () => {
    try {
      const { messages } = await chatApi.getHistory(sessionKey!);
      const formattedMessages: ChatMessage[] = messages.map(msg => ({
        id: msg.id,
        type: msg.isUser ? 'user' : 'assistant',
        content: msg.isUser ? msg.message : msg.response,
        timestamp: new Date(msg.timestamp)
      }));
      setChatMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploading(true);
    try {
      const result = await documentsApi.upload(sessionKey!, files);
      if (result.documents?.length > 0) {
        notify.success('Yükleme Başarılı', `${result.documents.length} dosya yüklendi`);
        await loadDocuments();
      }
    } catch (error: any) {
      notify.error('Yükleme Hatası', error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const runAnalysis = async (analysisType: string) => {
    if (documents.filter(doc => doc.status === 'READY').length === 0) {
      notify.error('Analiz Hatası', 'Analiz için hazır belge bulunamadı');
      return;
    }

    setRunningAnalysis(analysisType);
    const tool = ANALYSIS_TOOLS.find(t => t.key === analysisType);

    // Add analysis start message
    const startMessage: ChatMessage = {
      id: `analysis-start-${Date.now()}`,
      type: 'assistant',
      content: `${tool?.icon} ${tool?.label} analizi başlatılıyor... (Tahmini süre: ${tool?.time})`,
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, startMessage]);

    try {
      const result = await analysisApi.generate(sessionKey!, analysisType);

      // Add analysis result message
      const resultMessage: ChatMessage = {
        id: result.id,
        type: 'analysis',
        content: result.content,
        timestamp: new Date(),
        analysisType: result.type,
        analysisTitle: result.title
      };

      setChatMessages(prev => [...prev.slice(0, -1), resultMessage]); // Replace start message
      notify.success('Analiz Tamamlandı', tool?.label + ' analizi hazır');

    } catch (error: any) {
      // Remove start message and show error
      setChatMessages(prev => prev.slice(0, -1));
      notify.error('Analiz Hatası', error.message);
    } finally {
      setRunningAnalysis(null);
    }
  };

  const sendChatMessage = async () => {
    if (!chatMessage.trim() || sendingMessage) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: chatMessage.trim(),
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatMessage('');
    setSendingMessage(true);

    try {
      const response = await chatApi.sendMessage(sessionKey!, userMessage.content);

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        type: 'assistant',
        content: response.response,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      notify.error('Mesaj Hatası', error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await documentsApi.delete(documentId, sessionKey!);
      notify.success('Belge Silindi', 'Belge başarıyla kaldırıldı');
      await loadDocuments();
    } catch (error: any) {
      notify.error('Silme Hatası', error.message);
    }
  };

  const saveCaseInfo = async () => {
    try {
      await sessionApi.update(sessionKey!, {
        clientName: caseInfo.clientName,
        counterParty: caseInfo.counterParty,
        casePosition: caseInfo.casePosition
      });

      await loadSession(); // Reload to get updated data
      setEditingCaseInfo(false);
      notify.success('Güncellendi', 'Taraf bilgileri kaydedildi');
    } catch (error: any) {
      notify.error('Kaydetme Hatası', error.message);
    }
  };

  const createNewSession = async () => {
    try {
      const result = await sessionApi.create({});
      navigate(`/strategy/${result.sessionKey}`);
      notify.success('Yeni Oturum', 'Temiz bir oturumla başlıyorsunuz');
    } catch (error: any) {
      notify.error('Oturum Hatası', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-80 bg-white shadow-lg border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">Strateji Geliştirici</h1>
              <p className="text-sm text-gray-500">Analiz Araçları</p>
            </div>
            <button
              onClick={createNewSession}
              className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors flex items-center gap-1.5"
              title="Temiz bir oturumla yeniden başla"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              YENİ
            </button>
          </div>
        </div>

        {/* Document Upload Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">📤 Dosya Yükleme</h3>
              {uploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>}
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => document.getElementById('file-input')?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="text-gray-600">
                <p className="text-sm">Dosya Seç veya Sürükle</p>
                <p className="text-xs text-gray-400 mt-1">PDF, DOCX, TXT, MD</p>
              </div>
            </div>

            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf,.docx,.txt,.md"
              className="hidden"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
            />
          </div>

          {/* Documents List */}
          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{doc.originalName}</p>
                    <div className="flex items-center space-x-2 text-gray-500">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        doc.status === 'READY' ? 'bg-green-500' :
                        doc.status === 'PROCESSING' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}></span>
                      <span>{doc.status === 'READY' ? 'Hazır' : doc.status === 'PROCESSING' ? 'İşleniyor' : 'Hata'}</span>
                    </div>
                  </div>
                  {doc.status === 'READY' && (
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="text-red-500 hover:text-red-700 ml-2"
                      title="Sil"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Case Info */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">⚖️ Taraf Bilgileri</h3>
            <button
              onClick={() => {
                if (editingCaseInfo) {
                  saveCaseInfo();
                } else {
                  setEditingCaseInfo(true);
                }
              }}
              className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              {editingCaseInfo ? 'Kaydet' : 'Düzenle'}
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Müvekkil Adı</p>
              {editingCaseInfo ? (
                <input
                  type="text"
                  value={caseInfo.clientName}
                  onChange={(e) => setCaseInfo({ ...caseInfo, clientName: e.target.value })}
                  placeholder="Müvekkil adını giriniz"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              ) : (
                <p className="font-medium text-gray-900">{session?.clientName || 'Belirtilmemiş'}</p>
              )}
            </div>

            <div>
              <p className="text-gray-500 mb-1">Karşı Taraf Adı</p>
              {editingCaseInfo ? (
                <input
                  type="text"
                  value={caseInfo.counterParty}
                  onChange={(e) => setCaseInfo({ ...caseInfo, counterParty: e.target.value })}
                  placeholder="Karşı taraf adını giriniz"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              ) : (
                <p className="font-medium text-gray-900">{session?.counterParty || 'Belirtilmemiş'}</p>
              )}
            </div>

            <div>
              <p className="text-gray-500 mb-1">Dava Pozisyonumuz</p>
              {editingCaseInfo ? (
                <select
                  value={caseInfo.casePosition}
                  onChange={(e) => setCaseInfo({ ...caseInfo, casePosition: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="PLAINTIFF">Davacı</option>
                  <option value="DEFENDANT">Davalı</option>
                </select>
              ) : (
                <p className="font-medium text-gray-900">
                  {session?.casePosition === 'PLAINTIFF' ? 'Davacı' :
                   session?.casePosition === 'DEFENDANT' ? 'Davalı' : 'Belirtilmemiş'}
                </p>
              )}
            </div>

            {editingCaseInfo && (
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={saveCaseInfo}
                  className="flex-1 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingCaseInfo(false);
                    setCaseInfo({
                      clientName: session?.clientName || '',
                      counterParty: session?.counterParty || '',
                      casePosition: session?.casePosition || ''
                    });
                  }}
                  className="flex-1 px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                >
                  İptal
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Analysis Tools */}
        <div className="flex-1 p-6 overflow-y-auto">
          <h3 className="text-sm font-medium text-gray-900 mb-4">🔧 Analiz Araçları</h3>

          <div className="grid grid-cols-2 gap-2">
            {ANALYSIS_TOOLS.map((tool) => (
              <button
                key={tool.key}
                onClick={() => runAnalysis(tool.key)}
                disabled={runningAnalysis === tool.key || documents.filter(doc => doc.status === 'READY').length === 0}
                className={`flex flex-col items-center p-3 rounded-lg border text-xs font-medium transition-colors ${
                  runningAnalysis === tool.key
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : documents.filter(doc => doc.status === 'READY').length === 0
                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <span className="text-lg mb-1">{tool.icon}</span>
                <span className="text-center">{tool.label}</span>
                <span className="text-xs text-gray-500 mt-1">{tool.time}</span>
                {runningAnalysis === tool.key && (
                  <div className="mt-1 animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">💬 Soru & Cevap - GÜNCEL VERSİYON
                <button
                  onClick={() => {
                    if (messagesContainerRef.current) {
                      const container = messagesContainerRef.current;
                      container.scrollTop = container.scrollHeight;
                      console.log('Manuel scroll triggered');
                    }
                  }}
                  className="ml-4 px-2 py-1 text-xs bg-red-500 text-white rounded"
                >
                  SCROLL TEST
                </button>
              </h2>
              <p className="text-sm text-gray-500">
                {documents.filter(doc => doc.status === 'READY').length} belge hazır
                {chatMessages.length > 0 && ` • ${chatMessages.length} mesaj`}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                GPT-4o
              </span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {chatMessages.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🤖</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Hukuki sorununuzu yazın</h3>
              <p className="text-gray-500">Asistanımız belgelerden yararlanarak size yardımcı olacak</p>
            </div>
          ) : (
            chatMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-lg p-4 ${
                    message.type === 'user'
                      ? 'bg-blue-500 text-white'
                      : message.type === 'analysis'
                      ? 'bg-yellow-50 border-l-4 border-yellow-400'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  {message.type === 'analysis' && (
                    <div className="flex items-center mb-2 text-sm font-medium text-yellow-800">
                      <span>{message.analysisTitle}</span>
                    </div>
                  )}
                  <div className={`text-sm whitespace-pre-wrap ${
                    message.type === 'user' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {message.content}
                  </div>
                  <div className={`text-xs mt-2 ${
                    message.type === 'user' ? 'text-blue-200' : 'text-gray-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message Input */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="flex items-end space-x-3">
            <div className="flex-1">
              <textarea
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                placeholder="Hukuki sorunuzu yazın, asistanınız bu belgelerden yararlanarak size yardımcı olacak..."
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
                disabled={sendingMessage}
              />
            </div>
            <button
              onClick={sendChatMessage}
              disabled={!chatMessage.trim() || sendingMessage}
              className="bg-blue-500 text-white rounded-lg px-4 py-2 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {sendingMessage ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewStrategySessionPage;